import { FC, useEffect, useMemo } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { Box, Paper } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { DI_TOKENS } from '@constants';
import {
  AssessmentWorkflowHeader,
  ClassificationWarningModal,
  ExitAssessmentModal,
  ProgressFlow,
  ProgressFlowSkeleton,
  ReassessmentBanner,
  SaveChangesModal,
  useGetAssessmentById,
  useUpdateAssessmentTitle,
} from '@features/assessment';
import {
  findDomainById,
  mapToAssessmentHeaderViewModel,
  mapToPreviousVersionViewModel,
  mapToProgressFlowViewModel,
} from '@features/assessment/utils/assessment-mapper';
import { useInjection } from '@hooks';
import { ValidationHelpers } from '@libs/constants';
import { AssessmentWorkflowStore } from '@stores';

/**
 * Layout for assessment workflow routes.
 *
 * Owns the "steps part" that is shared across the assessment workflow:
 * - Re-assessment banner
 * - AssessmentWorkflowHeader (assessment name + Exit button)
 * - ProgressFlow stepper
 *
 * Per-domain content (DomainDetails, DomainNavigation, modals) is rendered
 * by the nested route via `<Outlet />`.
 *
 * Cross-cutting state (navigation guards, stepper disabled flag, exit modal)
 * flows through `AssessmentWorkflowStore`. The workflow page registers its
 * guard callbacks here on mount and the layout invokes them from the header
 * Exit button / stepper node clicks.
 */
export const AssessmentLayout: FC = observer(() => {
  const { assessmentId, domainId } = useParams<{
    assessmentId: string;
    domainId?: string;
  }>();
  const store = useInjection<AssessmentWorkflowStore>(
    DI_TOKENS.AssessmentWorkflowStore,
  );

  useEffect(() => {
    store.setAssessmentId(assessmentId ?? null);
    return () => store.setAssessmentId(null);
  }, [assessmentId, store]);

  const { data: assessment, isLoading } = useGetAssessmentById(
    { assessmentId: assessmentId ?? '' },
    {
      includeDomains: true,
      includePreviousAssessmentSummary: true,
    },
  );

  useEffect(() => {
    store.setAssessment(assessment ?? null);
    return () => store.setAssessment(null);
  }, [store, assessment]);

  const updateAssessmentTitleMutation = useUpdateAssessmentTitle();

  const headerViewModel = useMemo(
    () => mapToAssessmentHeaderViewModel(assessment),
    [assessment],
  );

  const progressFlowViewModel = useMemo(
    () => mapToProgressFlowViewModel(assessment),
    [assessment],
  );

  const previousVersionViewModel = useMemo(
    () =>
      mapToPreviousVersionViewModel(
        assessment?.previousAssessmentSummary,
        undefined,
        undefined,
      ),
    [assessment?.previousAssessmentSummary],
  );

  const selectedIndex = useMemo(() => {
    if (!domainId) return progressFlowViewModel.currentDomainIndex;

    if (ValidationHelpers.isUUID(domainId)) {
      const domain = findDomainById(assessment, domainId);
      return domain?.frameworkDomain?.orderIndex ?? 1;
    }

    const numericIndex = parseInt(domainId, 10);
    return !isNaN(numericIndex) && numericIndex >= 1
      ? numericIndex
      : progressFlowViewModel.currentDomainIndex;
  }, [domainId, assessment, progressFlowViewModel.currentDomainIndex]);

  const handleSaveAssessmentName = async (newName: string) => {
    if (!assessmentId) return;
    await updateAssessmentTitleMutation.mutateAsync({
      assessmentId,
      title: newName,
    });
  };

  const handleExitClick = () => {
    store.exitGuard?.();
  };

  const handleNodeClick = (index: number) => {
    store.nodeClickGuard?.(index);
  };

  const isAssessmentLoading = isLoading || !assessment;
  const { parentVersion, parentSubmittedAt } = previousVersionViewModel;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 1.5, sm: 2, md: 2 },
        height: '100%',
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      {parentVersion != null && (
        <ReassessmentBanner
          currentVersion={assessment?.version ?? 0}
          parentVersion={parentVersion}
          parentSubmittedAt={parentSubmittedAt}
        />
      )}

      {isAssessmentLoading ? (
        <ProgressFlowSkeleton />
      ) : (
        <Paper
          sx={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: { xs: 2, sm: 3, md: 4 },
            height: 'auto',
            paddingInline: 6,
            paddingTop: 3,
            paddingBottom: 10,
          }}
        >
          <AssessmentWorkflowHeader
            onExit={handleExitClick}
            assessmentName={headerViewModel.assessmentName}
            onSaveName={handleSaveAssessmentName}
            isSavingName={updateAssessmentTitleMutation.isPending}
          />
          <ProgressFlow
            items={progressFlowViewModel.assessmentItems}
            domainStates={progressFlowViewModel.domainStates}
            domainOptionalMap={progressFlowViewModel.domainOptionalMap}
            domainModifiedMap={progressFlowViewModel.domainModifiedMap}
            currentDomainIndex={selectedIndex}
            totalDomains={progressFlowViewModel.totalDomains}
            completionPercentage={progressFlowViewModel.completionPercentage}
            isNavigationDisabled={store.isNavigationDisabled}
            onNodeClick={handleNodeClick}
          />
        </Paper>
      )}

      <Outlet />

      <WorkflowModals />
    </Box>
  );
});

/**
 * The three workflow modals (SaveChanges / ClassificationWarning / Exit) are
 * rendered once at the layout level and read their open flags + handlers
 * straight from `AssessmentWorkflowStore`. The workflow hooks publish into
 * the store via `register*ModalHandlers`, so the page doesn't need to wire
 * any of this per render. When a handler bundle is `null` (e.g. when a hook
 * is unmounted), the modal stays hidden.
 */
const WorkflowModals: FC = observer(() => {
  const store = useInjection<AssessmentWorkflowStore>(
    DI_TOKENS.AssessmentWorkflowStore,
  );

  return (
    <>
      {store.saveChangesModalHandlers && (
        <SaveChangesModal
          open={store.isSaveChangesModalOpen}
          isLoading={store.saveChangesModalHandlers.isLoading}
          destinationType={store.saveChangesModalHandlers.destinationType}
          onSaveAndMove={store.saveChangesModalHandlers.onSaveAndMove as () => Promise<void>}
          onDiscardAndMove={
            store.saveChangesModalHandlers.onDiscardAndMove as () => Promise<void>
          }
          onStay={store.saveChangesModalHandlers.onStay}
        />
      )}

      {store.classificationWarningHandlers && (
        <ClassificationWarningModal
          open={store.isClassificationWarningOpen}
          issues={store.classificationWarningHandlers.issues}
          onSaveAnyway={store.classificationWarningHandlers.onSaveAnyway}
          onCancel={store.classificationWarningHandlers.onCancel}
        />
      )}

      {store.exitModalHandlers && (
        <ExitAssessmentModal
          open={store.isExitModalOpen}
          isLoading={store.exitModalHandlers.isLoading}
          completionPercentage={store.exitModalHandlers.completionPercentage}
          completedDomainsCount={store.exitModalHandlers.completedDomainsCount}
          totalDomains={store.exitModalHandlers.totalDomains}
          filesAttachedCount={store.exitModalHandlers.filesAttachedCount}
          onCancel={store.exitModalHandlers.onCancel}
          onSaveAndExit={store.exitModalHandlers.onSaveAndExit as () => Promise<void>}
          onDeleteAndExit={store.exitModalHandlers.onDeleteAndExit as () => Promise<void>}
        />
      )}
    </>
  );
});
